// src/AppContent.jsx
import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import initializeAnalytics from './analytics';
import useAuthStore from './store/authStore';
import { get } from './helpers/apiHelper';
import { mobileDebug } from './utils/mobileDebug';
import { useAdmin } from './contexts/AdminContext';
import ScrollToTop from './components/common/ScrollToTop';

// Importación de páginas y componentes globales
import Home from './pages/common/Home';
import Login from './pages/auth/Login';
import LoginEmpanadaClub from './templates/empanadaclub/pages/auth/LoginEmpanadaClub';
import Register from './pages/auth/Register';
import ProtectedRoute from './components/routes/ProtectedRoute';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import Sidebar from './components/common/Sidebar';
import PublicRoute from './components/routes/PublicRoute';
import TermsAndConditions from './pages/common/TermsAndConditions';
import CookiePolicyPage from './pages/common/CookiePolicyPage';
import PrivacyPage from './pages/common/PrivacyPage';
import LegalPage from './pages/common/LegalPage';
import PaymentResult from './pages/payment/PaymentResult';
import Settings from './pages/cliente/Settings';
import ResetPassword from './pages/auth/ResetPassword';
import ChangePassword from './pages/auth/ChangePassword';
import EmailVerification from './pages/auth/EmailVerification';
import SubscriptionPage from './pages/payment/SubscriptionPage';
import ProtectedLayout from './components/routes/ProtectedLayout';
import MarcasPage from './pages/tienda/MarcasPage';
import TiendasPage from './pages/tienda/TiendasPage';
import MarcasTiendasPage from './pages/tienda/MarcasTiendasPage';
import MarcasTiendasPageCustom from './templates/empanadaclub/pages/tienda/MarcasTiendasPage';
import BrandCustomizationPage from './pages/tienda/BrandCustomizationPage';
import InfografiaGeneratorPage from './pages/infografia/InfografiaGeneratorPage';
import CreateFormPage from './pages/tienda/CreateFormPage';
import TpvAgoraDocumentation from './pages/documentation/integraciones/TpvAgoraDocumentation';
import DocumentationIndex from './pages/documentation/DocumentationIndex';
import DocumentationWelcome from './pages/documentation/welcome/DocumentationWelcome';
import QuickStartIndex from './pages/documentation/quick-start/QuickStartIndex';
import QuickStartRegistro from './pages/documentation/quick-start/QuickStartRegistro';
import QuickStartLogin from './pages/documentation/quick-start/QuickStartLogin';
import QuickStartOnboarding from './pages/documentation/quick-start/QuickStartOnboarding';
import GestionIndex from './pages/documentation/gestion/GestionIndex';
import GestionMarcasTiendas from './pages/documentation/gestion/GestionMarcasTiendas';
import GestionClientes from './pages/documentation/gestion/GestionClientes';
import GestionProductos from './pages/documentation/gestion/GestionProductos';
import GestionVentas from './pages/documentation/gestion/GestionVentas';
import MarketingIndex from './pages/documentation/marketing/MarketingIndex';
import MarketingOfertas from './pages/documentation/marketing/MarketingOfertas';
import MarketingWinback from './pages/documentation/marketing/MarketingWinback';
import MarketingRecompensas from './pages/documentation/marketing/MarketingRecompensas';
import MarketingEmail from './pages/documentation/marketing/MarketingEmail';
import MarketingInfografias from './pages/documentation/marketing/MarketingInfografias';
import AnalyticsIndex from './pages/documentation/analytics/AnalyticsIndex';
import AnalyticsDashboard from './pages/documentation/analytics/AnalyticsDashboard';
import AnalyticsClientes from './pages/documentation/analytics/AnalyticsClientes';
import AnalyticsTiendas from './pages/documentation/analytics/AnalyticsTiendas';
import AnalyticsOfertas from './pages/documentation/analytics/AnalyticsOfertas';
import AnalyticsReporting from './pages/documentation/analytics/AnalyticsReporting';
import FeedbackIndex from './pages/documentation/feedback/FeedbackIndex';
import FeedbackEncuestas from './pages/documentation/feedback/FeedbackEncuestas';
import FeedbackAlertas from './pages/documentation/feedback/FeedbackAlertas';
import UsuariosIndex from './pages/documentation/usuarios/UsuariosIndex';
import UsuariosDueño from './pages/documentation/usuarios/UsuariosDueño';
import UsuariosTrabajador from './pages/documentation/usuarios/UsuariosTrabajador';
import UsuariosFranquiciado from './pages/documentation/usuarios/UsuariosFranquiciado';
import TpvAgoraUserGuide from './pages/documentation/integraciones/TpvAgoraUserGuide';
import ClientesList from './pages/cliente/ClientesList';
import CrearCliente from './pages/cliente/CrearCliente';
import HistoryPage from './pages/cliente/HistoryPage';
import TrabajadorLogin from './pages/auth/TrabajadorLogin';
import TrabajadorDashboard from './pages/trabajador/TrabajadorDashboard';
import TrabajadorRegister from './pages/auth/TrabajadorRegister';
import ProtectedTrabajadorRoute from './components/routes/ProtectedTrabajadorRoute';
import ProtectedFranquiciadoRoute from './components/routes/ProtectedFranquiciadoRoute';
import FranquiciadoLogin from './pages/auth/FranquiciadoLogin';
import FranquiciadoRegister from './pages/auth/FranquiciadoRegister';
import FranquiciadoResetPassword from './pages/auth/FranquiciadoResetPassword';
import FranquiciadoChangePassword from './pages/auth/FranquiciadoChangePassword';
import FranquiciadoDashboard from './pages/franquiciado/FranquiciadoDashboard';
import PricingPage from './pages/common/PricingPage';
import FeaturesPage from './pages/common/FeaturesPage';
import AboutPage from './pages/common/AboutPage';

// Admin Panel
import AdminPanel from './pages/admin/AdminPanel';
import ProtectedAdminRoute from './components/routes/ProtectedAdminRoute';
import DashboardRedirect from './components/routes/DashboardRedirect';

// Encuestas y Feedback
import DashboardSurveys from './pages/feedback/DashboardSurveys';
import SurveyCreator from './pages/feedback/SurveyCreator';
import SurveyResults from './pages/feedback/SurveyResults';
import SurveyPage from './pages/feedback/SurveyPage';
import CookieConsent from './components/common/CookieConsent';
import TicketView from './pages/cliente/TicketView';
import TicketViewWrapper from './components/routes/TicketViewWrapper';
import FranquiciadoRegisterWrapper from './components/routes/FranquiciadoRegisterWrapper';
import HeaderTrabajadores from './components/common/HeaderTrabajadores';
import HeaderFranquiciados from './components/common/HeaderFranquiciados';

// Estadísticas
import StatisticsPage from './pages/statistics/StatisticsPage';
import StoreStatisticsPage from './pages/statistics/StoreStatisticsPage';
import CustomerStatisticsPage from './pages/statistics/CustomerStatisticsPage';
import ClientesPage from './pages/cliente/ClientesPage';
import ImportClientesPage from './pages/cliente/ImportClientesPage';
import PrizeHistoryPage from './pages/cliente/PrizeHistoryPage';
import InventarioPage from './pages/inventario/InventarioPage';
import AlertModal from './components/modals/AlertModal';
import AlertPage from './pages/alertas/AlertPage';
import RewardsPage from './pages/rewards/RewardsPage';

// Campaña GameLoyalty
import CampaignManagementPage from './pages/gameLoyalty/CampaignManagementPage';
import CampaignPublicPage from './pages/gameLoyalty/CampaignPublicPage';
import CampaignDetailPage from './pages/gameLoyalty/CampaignDetailPage';
import CampaignPublicEmbedPage from './pages/gameLoyalty/CampaignPublicEmbedPage';
import EmbedRegistroPage from './pages/embed/EmbedRegistroPage';
import EmbedPage from './pages/embed/EmbedPage';
import EmbedFormPage from './pages/embed/EmbedFormPage';

// Gestión general de campañas
import CampaignsManagementPage from './pages/campaigns/CampaignsManagementPage';

// Gestión de ofertas
import { OfertasPage, OfertasMetricsPage } from './pages/ofertas';
import SocialContentPage from './pages/socialContent/SocialContentPage';

// WinBack Automático
import WinbackDashboardPage from './pages/winback/WinbackDashboardPage';
import PostsHistoryPage from './pages/socialContent/PostsHistoryPage';

// Email Marketing
import EmailMarketingDashboard from './pages/emailMarketing/EmailMarketingDashboard';
import CampaignsListPage from './pages/emailMarketing/CampaignsListPage';
import CampaignCreatePage from './pages/emailMarketing/CampaignCreatePage';
import CampaignStatsPage from './pages/emailMarketing/CampaignStatsPage';
import CampaignProgressPage from './pages/emailMarketing/CampaignProgressPage';
import ContactsListPage from './pages/emailMarketing/ContactsListPage';
import ContactsRFMPage from './pages/emailMarketing/ContactsRFMPage';
import UnsubscribePage from './pages/emailMarketing/UnsubscribePage';

// Gestión de ventas
import DataSellsPage from './pages/dataSells/DataSellsPage';

// Layout Bare para contenido embebible
import BareLayout from './components/layouts/BareLayout';
import DashboardCustomizable from './components/dashboards/DashboardCustomizable';
import SegmentacionClientes from './pages/cliente/SegmentacionClientes';

// Comunicación
import InboxPage from './pages/communication/InboxPage';
import EmailConfigPage from './pages/communication/EmailConfigPage';
import CommunicationMetricsPage from './pages/communication/CommunicationMetricsPage';
import SendMessagePage from './pages/communication/SendMessagePage';
import SchedulePage from './pages/communication/SchedulePage';
import CommunicationDashboard from './pages/communication/CommunicationDashboard';
import QRRegisterPage from './pages/public/QRRegisterPage';
import Blog from './pages/blog/Blog';
import BlogPost from './pages/blog/BlogPost';

// import AsistenteConfiguracionModal from "./components/asistenteConfiguracion/AsistenteConfiguracionModal";
import MainMenu from './components/common/MainMenu';
import UnderConstruction from './pages/common/UnderConstruction';

// Templates personalizados
import { shouldUseCustomTemplate, CUSTOM_TEMPLATE_MARCA_ID } from './utils/customTemplateHelper';
import MainMenuCustom from './templates/empanadaclub/MainMenu';
import HeaderCustom from './templates/empanadaclub/Header';
import FooterCustom from './templates/empanadaclub/Footer';
import HeaderFranquiciadosCustom from './templates/empanadaclub/components/common/HeaderFranquiciados';
import FranquiciadoLoginCustom from './templates/empanadaclub/pages/auth/FranquiciadoLogin';
import FranquiciadoRegisterCustom from './templates/empanadaclub/pages/auth/FranquiciadoRegister';
import FranquiciadoDashboardCustom from './templates/empanadaclub/pages/franquiciado/FranquiciadoDashboard';
import ProtectedFranquiciadoRouteCustom from './templates/empanadaclub/components/routes/ProtectedFranquiciadoRoute';
import DashboardAnalyticsPage from './pages/statistics/DashboardAnalyticsPage';
import ReportingPage from './pages/statistics/ReportingPage';
import WhatsAppConfigurationPage from './pages/communication/WhatsAppConfigurationPage';
import ReferralCampaignManagementPage from './pages/referralCampaign/ReferralCampaignManagementPage';

// Archetype System
import { ArchetypeProvider, useArchetype } from './contexts/ArchetypeContext';
import ArchetypeModal from './components/archetype/ArchetypeModal';
import ArchetypeTest from './components/archetype/ArchetypeTest';

// Error Handling
import ErrorBoundary from './components/common/ErrorBoundary';
import './utils/mobileErrorDebug';

// Marketing Chain
import MarketingChainPage from './pages/MarketingChainPage';
import MarketingChainHistoryPage from './pages/MarketingChainHistoryPage';
import StrategyDetailPage from './pages/StrategyDetailPage';

// Notificaciones
import { NotificationProvider } from './contexts/NotificationContext';
import { AdminProvider } from './contexts/AdminContext';
import NotificationSettings from './pages/NotificationSettings/NotificationSettings';
import SendNotifications from './pages/SendNotifications/SendNotifications';
import NotificationHistoryPage from './pages/NotificationHistory/NotificationHistoryPage';
import NotificationTemplatesPage from './pages/NotificationTemplates/NotificationTemplatesPage';
import OnboardingPage from './pages/onboarding/OnboardingPage';
import ProductosPublicPage from './pages/public/ProductosPublicPage';
import FindTicketPage from './pages/public/FindTicketPage';
import TpvPage from './pages/tpv/TpvPage';
import AccumulatePointsPage from './pages/cliente/AccumulatePointsPage';

// Nuevo componente NotFound
const NotFound = () => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-lg text-center">
            <div className="text-6xl text-gray-400 mb-4">404</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Página no encontrada</h2>
            <p className="text-gray-600 mb-6">Lo sentimos, la página que buscas no existe.</p>
            <a href="/" className="inline-block bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-dark transition-colors">
                Volver al inicio
            </a>
        </div>
    </div>
);

const AppContentInner = () => {
    const { trabajador, franquiciado, user } = useAuthStore();
    const { showQuiz } = useArchetype();
    const { isAdminMode } = useAdmin();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [useCustomTemplate, setUseCustomTemplate] = useState(false);
    const [marcas, setMarcas] = useState([]);

    const location = useLocation();

    const isEmbedRoute = location.pathname.startsWith('/game-loyalty/embed/') || location.pathname.startsWith('/embed/');
    const isPublicQRRegister = location.pathname.startsWith('/qr-register');
    const isTicketRoute = location.pathname.startsWith('/tickets/');
    const isSurveyRoute = location.pathname.startsWith('/surveys/');
    const isPublicProductsRoute = location.pathname.startsWith('/productos/');
    const isFindTicketRoute = location.pathname.startsWith('/find-ticket/');
    const isLoginEmpanadaClub = location.pathname === '/login-empanadaclub';
    const isFranquiciadoLoginEmpanadaClub = location.pathname === '/franquiciado/login-empanadaclub';
    const isFranquiciadoRegister = location.pathname === '/franquiciado/register';

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    useEffect(() => {
        const fetchMarcasAndAlerts = async () => {
            try {
                if (user?.userId) {
                    const responseMarcas = await get(`marcas/user/${user.userId}`);
                    setMarcas(responseMarcas);

                    // Verificar si debe usar template personalizado
                    const shouldUse = shouldUseCustomTemplate(user.userId, responseMarcas);
                    setUseCustomTemplate(shouldUse);

                    if (responseMarcas.length > 0) {
                        const alertPromises = responseMarcas.map(async (marca) => {
                            try {
                                // Para el modal, solo necesitamos alertas no leídas (comportamiento por defecto)
                                const result = await get(`alertas/marca/${marca._id}`);
                                // El backend ahora devuelve { alertas: [...], pagination: {...} }
                                return result.alertas || result;
                            } catch (error) {
                                console.error('Error obteniendo alertas para marca:', error);
                                return [];
                            }
                        });
                        const allAlerts = (await Promise.all(alertPromises)).flat();

                        // El backend ya filtra solo alertas no leídas por defecto, pero mantenemos el filtro por seguridad
                        const unreadAlerts = allAlerts.filter(alert => !alert.leida);
                        setAlerts(unreadAlerts);

                        // Mostrar modal solo si hay alertas no leídas
                        if (unreadAlerts.length > 0) {
                            setShowModal(true);
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching marcas or alerts:', error);

                // En desarrollo, mostrar más información del error
                if (process.env.NODE_ENV === 'development') {
                    console.error('Error details:', {
                        message: error.message,
                        stack: error.stack,
                        user: user?.userId,
                        isMobile: mobileDebug.isMobile()
                    });
                }
            }
        };

        fetchMarcasAndAlerts();
    }, [user]);

    // Verificar template personalizado para franquiciados
    useEffect(() => {
        const checkFranquiciadoTemplate = async () => {
            try {
                if (franquiciado?.franquiciadoId || franquiciado?._id) {
                    const franquiciadoId = franquiciado.franquiciadoId || franquiciado._id;
                    // Obtener las tiendas del franquiciado
                    const tiendas = await get(`franquiciados/${franquiciadoId}/tiendas`);

                    // Verificar si alguna tienda pertenece a la marca personalizada
                    if (tiendas && tiendas.length > 0) {
                        // Obtener las marcas de las tiendas
                        const marcasIds = tiendas
                            .map(tienda => tienda.marca?._id || tienda.marca)
                            .filter(Boolean);

                        // Verificar si alguna marca coincide con la marca personalizada
                        const hasCustomMarca = marcasIds.some(marcaId => marcaId === CUSTOM_TEMPLATE_MARCA_ID);

                        if (hasCustomMarca) {
                            setUseCustomTemplate(true);
                        }
                    }
                }
            } catch (error) {
                console.error('Error checking franquiciado template:', error);
            }
        };

        checkFranquiciadoTemplate();
    }, [franquiciado]);

    useEffect(() => {
        initializeAnalytics();

        // Ejecutar debug de móvil en desarrollo
        if (process.env.NODE_ENV === 'development') {
            mobileDebug.runAllChecks();
        }
    }, []);

    return (
        <div>
            <ScrollToTop />

            {showModal && alerts.length > 0 && (
                <AlertModal
                    alerts={alerts}
                    onClose={() => setShowModal(false)}
                    onAlertsRead={() => setAlerts([])}
                />
            )}

            {/* ArchetypeModal solo para usuarios logueados */}
            {user?.userId && (
                <ArchetypeModal
                    isOpen={false}
                    onClose={() => { }}
                />
            )}

            {/* Mostrar header y sidebar solo si NO es ruta especial */}
            {(!isEmbedRoute && !isPublicQRRegister && !isTicketRoute && !isSurveyRoute && !isPublicProductsRoute && !isFindTicketRoute && !isLoginEmpanadaClub && !isFranquiciadoLoginEmpanadaClub && !isFranquiciadoRegister && !location.pathname.startsWith('/verify')) && (
                <>
                    {trabajador ? (
                        // Solo mostrar header de trabajadores si NO es template personalizado
                        !useCustomTemplate ? <HeaderTrabajadores /> : null
                    ) : franquiciado ? (
                        useCustomTemplate ? <HeaderFranquiciadosCustom /> : <HeaderFranquiciados />
                    ) : (
                        <>
                            {useCustomTemplate ? (
                                <HeaderCustom toggleSidebar={toggleSidebar} />
                            ) : (
                                <Header toggleSidebar={toggleSidebar} />
                            )}
                            {user?.userId && !isAdminMode && !location.pathname.startsWith('/onboarding') ? (
                                useCustomTemplate ? <MainMenuCustom /> : <MainMenu />
                            ) : ''}
                        </>
                    )}
                    <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
                </>
            )}

            <main>
                <Routes>
                    {/* Rutas especiales que no necesitan layout */}
                    <Route path="/verify" element={<EmailVerification />} />
                    <Route path="/qr-register/:tiendaId" element={<QRRegisterPage />} />
                    <Route path="/productos/:marcaId" element={<ProductosPublicPage />} />
                    <Route path="/find-ticket/:marcaId" element={<FindTicketPage />} />
                    <Route path="/game-loyalty/public/:campaignId" element={<CampaignPublicPage />} />
                    <Route path="/game-loyalty/embed/:campaignId" element={<CampaignPublicEmbedPage />} />
                    <Route path="/surveys/:surveyId" element={<SurveyPage />} />
                    <Route path="/tickets/:ticketUrl" element={<TicketViewWrapper />} />
                    <Route path="/email-marketing/unsubscribe/:token" element={<UnsubscribePage />} />

                    {/* Rutas de embed - sin header/footer */}
                    <Route path="/embed/registro" element={<EmbedRegistroPage />} />
                    <Route path="/embed/:tiendaId" element={<EmbedPage />} />
                    <Route path="/embed/global/:embedCode" element={<EmbedFormPage />} />
                    <Route path="/embed/code/:embedCode" element={<EmbedPage />} />

                    {/* Rutas públicas */}
                    <Route element={<PublicRoute />}>
                        <Route path="/" element={<Home />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/login-empanadaclub" element={<LoginEmpanadaClub />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/change-password" element={<ChangePassword />} />
                        <Route path="/email-verification" element={<EmailVerification />} />
                        <Route path="/pricing" element={<PricingPage />} />
                        <Route path="/features" element={<FeaturesPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/terminos" element={<TermsAndConditions />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/legal" element={<LegalPage />} />
                        <Route path="/politica-de-cookies" element={<CookiePolicyPage />} />
                        <Route path="/payment-result" element={<PaymentResult />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/blog/:slug" element={<BlogPost />} />
                        {/* Rutas de trabajador - solo si NO es template personalizado */}
                        {!useCustomTemplate && (
                            <>
                                <Route path="/trabajador/login" element={<TrabajadorLogin />} />
                                <Route path="/trabajador/register" element={<TrabajadorRegister />} />
                            </>
                        )}
                        {/* Rutas de franquiciado - usar componentes personalizados si corresponde */}
                        <Route path="/franquiciado/login" element={<FranquiciadoLogin />} />
                        <Route path="/franquiciado/login-empanadaclub" element={<FranquiciadoLoginCustom />} />
                        <Route path="/franquiciado/register" element={<FranquiciadoRegisterWrapper />} />
                        <Route path="/franquiciado/reset-password" element={<FranquiciadoResetPassword />} />
                        <Route path="/franquiciado/change-password" element={<FranquiciadoChangePassword />} />

                        {/* Documentación - Accesible sin login */}
                        <Route path="/documentation" element={<DocumentationIndex />} />
                        <Route path="/documentation/welcome" element={<DocumentationWelcome />} />
                        <Route path="/documentation/quick-start" element={<QuickStartIndex />} />
                        <Route path="/documentation/quick-start/registro" element={<QuickStartRegistro />} />
                        <Route path="/documentation/quick-start/login" element={<QuickStartLogin />} />
                        <Route path="/documentation/quick-start/onboarding" element={<QuickStartOnboarding />} />
                        <Route path="/documentation/gestion" element={<GestionIndex />} />
                        <Route path="/documentation/gestion/marcas-tiendas" element={<GestionMarcasTiendas />} />
                        <Route path="/documentation/gestion/clientes" element={<GestionClientes />} />
                        <Route path="/documentation/gestion/productos" element={<GestionProductos />} />
                        <Route path="/documentation/gestion/ventas" element={<GestionVentas />} />
                        <Route path="/documentation/marketing" element={<MarketingIndex />} />
                        <Route path="/documentation/marketing/ofertas" element={<MarketingOfertas />} />
                        <Route path="/documentation/marketing/winback" element={<MarketingWinback />} />
                        <Route path="/documentation/marketing/recompensas" element={<MarketingRecompensas />} />
                        <Route path="/documentation/marketing/email-marketing" element={<MarketingEmail />} />
                        <Route path="/documentation/marketing/infografias" element={<MarketingInfografias />} />

                        {/* Analytics */}
                        <Route path="/documentation/analytics" element={<AnalyticsIndex />} />
                        <Route path="/documentation/analytics/dashboard" element={<AnalyticsDashboard />} />
                        <Route path="/documentation/analytics/clientes" element={<AnalyticsClientes />} />
                        <Route path="/documentation/analytics/tiendas" element={<AnalyticsTiendas />} />
                        <Route path="/documentation/analytics/ofertas" element={<AnalyticsOfertas />} />
                        <Route path="/documentation/analytics/reporting" element={<AnalyticsReporting />} />

                        {/* Feedback y Alertas */}
                        <Route path="/documentation/feedback" element={<FeedbackIndex />} />
                        <Route path="/documentation/feedback/encuestas" element={<FeedbackEncuestas />} />
                        <Route path="/documentation/feedback/alertas" element={<FeedbackAlertas />} />

                        {/* Usuarios */}
                        <Route path="/documentation/usuarios" element={<UsuariosIndex />} />
                        <Route path="/documentation/usuarios/dueño" element={<UsuariosDueño />} />
                        <Route path="/documentation/usuarios/trabajador" element={<UsuariosTrabajador />} />
                        <Route path="/documentation/usuarios/franquiciado" element={<UsuariosFranquiciado />} />

                        {/* Integraciones */}
                        <Route path="/documentation/integraciones/tpv-agora" element={<TpvAgoraUserGuide />} />
                        <Route path="/documentation/integraciones/tpv-agora/tecnico" element={<TpvAgoraDocumentation />} />
                    </Route>

                    {/* Rutas protegidas */}
                    <Route element={<ProtectedRoute />}>
                        <Route element={<ProtectedLayout />}>
                            {/* Ruta principal que maneja todos los casos */}
                            <Route path="/dashboard" element={<DashboardRedirect />} />
                            <Route path="/dashboard/custom" element={user ? <DashboardCustomizable userId={user.userId} /> : <Navigate to="/login" />} />

                            {/* Ruta de onboarding */}
                            <Route path="/onboarding" element={<OnboardingPage />} />

                            {/* Configuración y suscripción */}
                            <Route path="/settings" element={<Settings />} />
                            <Route path="/subscribe" element={<SubscriptionPage />} />

                            {/* Gestión de marcas y tiendas */}
                            <Route path="/marcas" element={<MarcasPage />} />
                            <Route path="/tiendas" element={<TiendasPage />} />
                            <Route path="/marcas-tiendas" element={useCustomTemplate ? <MarcasTiendasPageCustom /> : <MarcasTiendasPage />} />
                            <Route path="/marcas/:marcaId/personalizacion" element={<BrandCustomizationPage />} />
                            <Route path="/tienda/:marcaId/infografia" element={<InfografiaGeneratorPage />} />
                            <Route path="/create-form" element={<CreateFormPage />} />

                            {/* Gestión de clientes */}
                            <Route path="/clientes" element={<ClientesPage />} />
                            <Route path="/clientes/importar" element={<ImportClientesPage />} />
                            <Route path="/clientes/segmentacion/:marcaId" element={<SegmentacionClientes />} />
                            <Route path="/tiendas/:tiendaId/clientes" element={<ClientesList />} />
                            <Route path="/tiendas/:tiendaId/clientes/create" element={<CrearCliente />} />
                            <Route path="/history/:clienteId" element={<HistoryPage />} />
                            <Route path="/canjeos/:clienteId" element={<PrizeHistoryPage />} />

                            {/* TPV - Terminales de Punto de Venta */}
                            <Route path="/tiendas/:tiendaId/tpv" element={<TpvPage />} />
                            <Route path="/tiendas/:tiendaId/accumulate-points" element={<AccumulatePointsPage />} />

                            {/* Inventario y recompensas */}
                            <Route path="/inventario" element={<InventarioPage />} />
                            <Route path="/rewards" element={<RewardsPage />} />
                            <Route path="/alertas" element={<AlertPage />} />

                            {/* Gestión de ventas */}
                            <Route path="/data-sells" element={<DataSellsPage />} />

                            {/* Comunicación */}
                            <Route path="/communication/dashboard" element={<CommunicationDashboard />} />
                            <Route path="/communication/inbox" element={<InboxPage />} />
                            <Route path="/communication/config" element={<EmailConfigPage />} />
                            <Route path="/communication/metrics" element={<CommunicationMetricsPage />} />
                            <Route path="/communication/send" element={<SendMessagePage />} />
                            <Route path="/communication/schedule" element={<SchedulePage />} />
                            <Route path="/whatsapp/configurar" element={<WhatsAppConfigurationPage />} />

                            {/* Campañas y GameLoyalty */}
                            <Route path="/game-loyalty/manage" element={<CampaignManagementPage />} />
                            <Route path="/game-loyalty/detail/:campaignId" element={<CampaignDetailPage />} />
                            <Route path="/referral-campaign/manage" element={<ReferralCampaignManagementPage />} />
                            <Route path="/campaigns" element={<CampaignsManagementPage />} />

                            {/* Ofertas */}
                            <Route path="/ofertas" element={<OfertasPage />} />
                            <Route path="/ofertas/metrics" element={<OfertasMetricsPage />} />

                            {/* WinBack Automático */}
                            <Route path="/winback" element={<WinbackDashboardPage />} />

                            {/* Email Marketing */}
                            <Route path="/email-marketing" element={<EmailMarketingDashboard />} />
                            <Route path="/email-marketing/campaigns" element={<CampaignsListPage />} />
                            <Route path="/email-marketing/campaigns/new" element={<CampaignCreatePage />} />
                            <Route path="/email-marketing/campaigns/:id/edit" element={<CampaignCreatePage />} />
                            <Route path="/email-marketing/campaigns/:id/stats" element={<CampaignStatsPage />} />
                            <Route path="/email-marketing/campaigns/:id/progress" element={<CampaignProgressPage />} />
                            <Route path="/email-marketing/contacts" element={<ContactsListPage />} />
                            <Route path="/email-marketing/contacts/rfm" element={<ContactsRFMPage />} />

                            {/* Contenido Social */}
                            <Route path="/social-content" element={<SocialContentPage />} />
                            <Route path="/social-content/history" element={<PostsHistoryPage />} />

                            {/* Marketing Chain */}
                            <Route path="/marketing-chain" element={<MarketingChainPage />} />
                            <Route path="/marketing-chain/history" element={<MarketingChainHistoryPage />} />
                            <Route path="/marketing-chain/strategy/:sessionId" element={<StrategyDetailPage />} />

                            {/* Encuestas */}
                            <Route path="/dashboard-surveys" element={<DashboardSurveys />} />
                            <Route path="/create-survey" element={<SurveyCreator />} />
                            <Route path="/survey-results/:surveyId" element={<SurveyResults />} />

                            {/* Estadísticas */}
                            <Route path="/statistics/dashboard" element={<DashboardAnalyticsPage />} />
                            <Route path="/statistics/reporting" element={<ReportingPage />} />
                            <Route path="/statistics" element={<StatisticsPage />} />
                            <Route path="/statistics/store" element={<StoreStatisticsPage />} />
                            <Route path="/statistics/store/tienda/:tiendaId" element={<StoreStatisticsPage />} />
                            <Route path="/statistics/customer" element={<CustomerStatisticsPage />} />
                            <Route path="/statistics/customer/:clienteId" element={<CustomerStatisticsPage />} />
                            {/* Rutas alternativas para compatibilidad */}
                            <Route path="/store-statistics/tienda/:tiendaId" element={<StoreStatisticsPage />} />
                            <Route path="/customer-statistics/:clienteId" element={<CustomerStatisticsPage />} />

                            {/* Notificaciones */}
                            <Route path="/notifications/settings" element={<NotificationSettings />} />
                            <Route path="/notifications/send" element={<SendNotifications />} />
                            <Route path="/notifications/history" element={<NotificationHistoryPage />} />
                            <Route path="/notifications/templates" element={<NotificationTemplatesPage />} />

                            {/* Archetype System */}
                            <Route path="/archetype-test" element={<ArchetypeTest />} />
                        </Route>
                    </Route>

                    {/* Rutas protegidas de administrador */}
                    <Route element={<ProtectedAdminRoute />}>
                        <Route path="/admin" element={<AdminPanel />} />
                    </Route>

                    {/* Rutas protegidas de trabajador - solo si NO es template personalizado */}
                    {!useCustomTemplate && (
                        <Route element={<ProtectedTrabajadorRoute />}>
                            <Route path="/trabajador/:trabajadorId/dashboard" element={<TrabajadorDashboard />} />
                            <Route path="/trabajador/:trabajadorId/tiendas/:tiendaId/clientes" element={<ClientesList />} />
                            <Route path="/trabajador/:trabajadorId/tiendas/:tiendaId/clientes/create" element={<CrearCliente />} />
                        </Route>
                    )}

                    {/* Rutas protegidas de franquiciado */}
                    <Route element={useCustomTemplate ? <ProtectedFranquiciadoRouteCustom /> : <ProtectedFranquiciadoRoute />}>
                        <Route path="/franquiciado/:franquiciadoId/dashboard" element={useCustomTemplate ? <FranquiciadoDashboardCustom /> : <FranquiciadoDashboard />} />
                        <Route path="/franquiciado/:franquiciadoId/tiendas/:tiendaId/clientes" element={<ClientesList />} />
                        <Route path="/franquiciado/:franquiciadoId/tiendas/:tiendaId/clientes/create" element={<CrearCliente />} />
                        <Route path="/franquiciado/:franquiciadoId/estadisticas/:tiendaId" element={<StoreStatisticsPage />} />
                        <Route path="/franquiciado/:franquiciadoId/tiendas/:tiendaId/accumulate-points" element={<AccumulatePointsPage />} />
                    </Route>

                    {/* Layout Bare */}
                    <Route element={<BareLayout />}>
                        <Route path="/under-construction" element={<UnderConstruction />} />
                    </Route>

                    {/* 404 route */}
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </main>

            {/* Footer solo si NO es ruta especial */}
            {(!isEmbedRoute && !isPublicQRRegister && !isTicketRoute && !isSurveyRoute && !isPublicProductsRoute && !isFindTicketRoute && !isLoginEmpanadaClub && !isFranquiciadoLoginEmpanadaClub && !isFranquiciadoRegister && !location.pathname.startsWith('/verify')) && (
                <>
                    {useCustomTemplate ? <FooterCustom /> : <Footer />}
                    <CookieConsent />
                </>
            )}
        </div>
    );
};

const AppContent = () => {
    return (
        <ErrorBoundary>
            <AdminProvider>
                <NotificationProvider>
                    <ArchetypeProvider>
                        <AppContentInner />
                    </ArchetypeProvider>
                </NotificationProvider>
            </AdminProvider>
        </ErrorBoundary>
    );
};

export default AppContent;
